import { useEffect, useState, useCallback } from 'react';
import { loginAdmin, refreshToken, fetchProfile, logoutAdmin, verifyAdminTwoFactor, changeAdminPassword } from '../api/auth';
import {
  getLocalItem,
  getSessionItem,
  removeLocalItem,
  removeSessionItem,
  setLocalItem,
  setSessionItem,
} from '../utils/safeStorage';
import { resolvePermissionsFromPayload } from '../utils/adminPermissions';

const ACCESS_KEY = 'adminAccessToken';
const LEGACY_ACCESS_KEY = 'hv_admin_token';

const isAdminRoute = () => {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/admin');
};

const persistAdminToken = (token) => {
  if (!token) return;
  setSessionItem(ACCESS_KEY, token);
  setLocalItem(ACCESS_KEY, token);
  // Keep legacy key in sync for older admin modules.
  setSessionItem(LEGACY_ACCESS_KEY, token);
  setLocalItem(LEGACY_ACCESS_KEY, token);
};

const clearAdminTokens = () => {
  removeSessionItem(ACCESS_KEY);
  removeLocalItem(ACCESS_KEY);
  removeSessionItem(LEGACY_ACCESS_KEY);
  removeLocalItem(LEGACY_ACCESS_KEY);
};

export default function useAuth() {
  const [accessToken, setAccessToken] = useState(
    () =>
      getSessionItem(ACCESS_KEY) ||
      getLocalItem(ACCESS_KEY) ||
      getSessionItem(LEGACY_ACCESS_KEY) ||
      getLocalItem(LEGACY_ACCESS_KEY) ||
      ''
  );
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const applyAuthPayload = useCallback((payload, token) => {
    const admin = payload?.admin || payload;
    if (token) {
      persistAdminToken(token);
      setAccessToken(token);
    }
    setUser(admin || null);
    const resolved = resolvePermissionsFromPayload(payload || admin || {});
    setPermissions(resolved.permissions || []);
    setIsSuperAdmin(Boolean(resolved.isSuperAdmin));
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const applyToken = async (token) => {
        const me = await fetchProfile(token);
        applyAuthPayload(me, token);
      };
      if (accessToken) {
        try {
          const me = await fetchProfile(accessToken);
          applyAuthPayload(me, accessToken);
          return;
        } catch (err) {
          const refreshed = await refreshToken().catch(() => null);
          if (refreshed?.accessToken) {
            await applyToken(refreshed.accessToken);
            return;
          }
          throw err;
        }
      }
      if (!isAdminRoute()) return;
      const refreshed = await refreshToken();
      if (refreshed?.accessToken) {
        await applyToken(refreshed.accessToken);
      }
    } catch (err) {
      clearAdminTokens();
      setAccessToken('');
      setUser(null);
      setPermissions([]);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [accessToken, applyAuthPayload]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    const data = await loginAdmin(email, password);
    if (data?.twoFactorRequired) {
      return data;
    }
    applyAuthPayload(data, data.accessToken);
    return data;
  };

  const verifyTwoFactor = async (tempToken, token) => {
    const data = await verifyAdminTwoFactor(tempToken, token);
    applyAuthPayload(data, data.accessToken);
    return data;
  };

  const logout = async () => {
    if (accessToken) {
      try {
        await logoutAdmin(accessToken);
      } catch {
        // ignore
      }
    }
    clearAdminTokens();
    setAccessToken('');
    setUser(null);
    setPermissions([]);
    setIsSuperAdmin(false);
  };

  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    if (isSuperAdmin) return true;
    if (!permissions || permissions.length === 0) return false;
    if (Array.isArray(permission)) {
      return permission.some((perm) => permissions.includes(perm));
    }
    return permissions.includes(permission);
  }, [isSuperAdmin, permissions]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    if (!accessToken) {
      throw new Error('Not authenticated');
    }
    const result = await changeAdminPassword(accessToken, currentPassword, newPassword);
    await bootstrap();
    return result;
  }, [accessToken, bootstrap]);

  return {
    accessToken,
    user,
    permissions,
    isSuperAdmin,
    loading,
    login,
    verifyTwoFactor,
    hasPermission,
    changePassword,
    logout,
    refresh: bootstrap,
  };
}
